import * as THREE from 'three';
import { MiningCollector, MiningCollectorConfig } from './MiningCollector';
import { TransactionShip } from './TransactionShip';
import { MempoolNebula } from '../effects/MempoolNebula';
import { useGalaxyStore } from '../../core/state/GalaxyStore';

export interface MiningOperationsConfig {
  maxCollectors: number;
  miningPools: {
    name: string;
    hashrate: number;
    color: number;
    feePreference: 'high' | 'medium' | 'low' | 'any';
  }[];
  competitionIntensity: number; // How aggressively pools compete
  extractionRange: number; // Range from mempool center
}

/**
 * Phase 3: Mining Operations Manager
 * Coordinates multiple mining collectors competing for transaction selection
 */
export class MiningOperationsManager {
  private scene: THREE.Scene | THREE.Group;
  private collectors: Map<string, MiningCollector>;
  private miningGroup: THREE.Group;
  private config: MiningOperationsConfig;
  private lastUpdate: number = 0;
  private updateInterval: number = 200; // Update mining operations every 200ms
  private competitionTimer: number = 0;
  
  // Mining competition tracking
  private transactionCompetition: Map<string, string[]> = new Map(); // txId -> collectorIds competing
  private miningStatistics: {
    totalExtractions: number;
    averageExtractionTime: number;
    poolSuccessRates: Map<string, number>;
  };

  constructor(scene: THREE.Scene | THREE.Group, config: MiningOperationsConfig) {
    console.log('🏗️ Creating MiningOperationsManager with config:', config);
    this.scene = scene;
    this.config = config;
    this.collectors = new Map();
    this.miningGroup = new THREE.Group();
    this.miningGroup.name = 'MiningOperations';
    
    this.miningStatistics = {
      totalExtractions: 0,
      averageExtractionTime: 0,
      poolSuccessRates: new Map()
    };
    
    console.log('📍 Adding mining group to parent...');
    this.scene.add(this.miningGroup);
    console.log('🚀 Initializing mining collectors...');
    this.initializeMiningCollectors();
    console.log('✨ MiningOperationsManager fully initialized with', this.collectors.size, 'collectors');
    
    // Trigger validation of newly added interactive objects
    this.validateInteractiveObjects();
  }

  private initializeMiningCollectors(): void {
    const mempoolCenter = new THREE.Vector3(0, 0, 0);
    const collectorCount = Math.min(this.config.miningPools.length, this.config.maxCollectors);
    
    console.log(`🎛️ Creating ${collectorCount} mining collectors around mempool at range ${this.config.extractionRange}`);
    
    for (let i = 0; i < collectorCount; i++) {
      const poolConfig = this.config.miningPools[i];
      
      // Position collectors around mempool at extraction range
      const angle = (i / collectorCount) * Math.PI * 2;
      const collectorPosition = new THREE.Vector3(
        Math.cos(angle) * this.config.extractionRange,
        15 + Math.random() * 10, // Vary height for visual interest
        Math.sin(angle) * this.config.extractionRange
      );
      
      console.log(`📍 Positioning ${poolConfig.name} at angle ${(angle * 180 / Math.PI).toFixed(1)}° position:`, collectorPosition);
      
      const collectorConfig: MiningCollectorConfig = {
        poolName: poolConfig.name,
        hashrate: poolConfig.hashrate,
        position: collectorPosition,
        color: new THREE.Color(poolConfig.color),
        beamCount: this.calculateBeamCount(poolConfig.hashrate),
        targetingRange: 45, // Slightly beyond mempool outer radius
        extractionSpeed: this.calculateExtractionSpeed(poolConfig.hashrate),
        feePreference: poolConfig.feePreference
      };
      
      const collector = new MiningCollector(collectorConfig);
      this.collectors.set(collector.id, collector);
      
      console.log(`➕ Adding ${poolConfig.name} collector mesh to mining group...`);
      this.miningGroup.add(collector.getMesh());
      console.log(`📊 Mining group now has ${this.miningGroup.children.length} children`);
      
      // Initialize success rate tracking
      this.miningStatistics.poolSuccessRates.set(poolConfig.name, 0);
    }
    
    console.log(`🏁 All collectors initialized. Mining group total children: ${this.miningGroup.children.length}`);
  }

  private calculateBeamCount(hashrate: number): number {
    // More powerful miners can operate more simultaneous beams
    if (hashrate > 100000000000000) return 4; // 100 EH/s
    if (hashrate > 50000000000000) return 3;  // 50 EH/s
    if (hashrate > 20000000000000) return 2;  // 20 EH/s
    return 1;
  }

  private calculateExtractionSpeed(hashrate: number): number {
    // Higher hashrate = faster extraction
    return Math.min(hashrate / 50000000000000, 2.0);
  }

  /**
   * Phase 3: Main update loop for mining operations
   */
  public update(deltaTime: number, timeScale: number = 1.0, availableShips?: Map<string, TransactionShip>): void {
    const now = Date.now();
    if (now - this.lastUpdate < this.updateInterval) return;
    
    this.lastUpdate = now;
    this.competitionTimer += deltaTime;
    
    const store = useGalaxyStore.getState();
    const mempoolNebula = store.scene.activeMempoolNebula;
    
    // Use provided ships or empty map if none provided
    const ships = availableShips || new Map();
    
    if (!mempoolNebula || ships.size === 0) {
      // Still update collectors even if no ships available
      this.collectors.forEach(collector => {
        collector.update(deltaTime * timeScale);
      });
      return;
    }
    
    // Phase 3: Coordinate mining competition
    this.updateMiningCompetition(mempoolNebula, ships, deltaTime);
    
    // Update individual collectors
    this.collectors.forEach(collector => {
      collector.update(deltaTime * timeScale);
    });
    
    // Update mining statistics
    this.updateMiningStatistics();
  }

  private getAvailableShips(): Map<string, TransactionShip> {
    // Get ships from the DynamicObjectsManager - this method will be called
    // with ships passed from integrateWithShipManagement()
    // For now, return empty map as ships are passed via integrateWithShipManagement
    return new Map();
  }

  /**
   * Phase 3: Implement mining competition mechanics
   * Multiple collectors compete for the same high-value transactions
   */
  private updateMiningCompetition(
    mempoolNebula: MempoolNebula, 
    availableShips: Map<string, TransactionShip>,
    _deltaTime: number // Underscore prefix to indicate intentionally unused
  ): void {
    // Clear previous competition state
    this.transactionCompetition.clear();
    
    // Each collector scans for targets
    this.collectors.forEach(collector => {
      collector.scanForTargets(mempoolNebula, availableShips);
    });
    
    // Detect and resolve competition
    this.resolveCompetition(availableShips);
  }

  private resolveCompetition(_availableShips: Map<string, TransactionShip>): void {
    // Track which transactions are being targeted by multiple collectors
    const transactionTargets: Map<string, string[]> = new Map();
    
    // Collect all targeting information
    this.collectors.forEach(collector => {
      const activeTargets = collector.getActiveTargets();
      
      activeTargets.forEach((_target, shipId) => {
        if (!transactionTargets.has(shipId)) {
          transactionTargets.set(shipId, []);
        }
        transactionTargets.get(shipId)!.push(collector.id);
      });
    });
    
    // For each competed transaction, determine winner based on:
    // 1. Mining power (hashrate)
    // 2. Fee preference match
    // 3. Distance to target
    // 4. Current beam utilization
    
    transactionTargets.forEach((collectorIds, txId) => {
      if (collectorIds.length > 1) {
        const winningCollectorId = this.determineCompetitionWinner(txId, collectorIds);
        const losingCollectorIds = collectorIds.filter(id => id !== winningCollectorId);
        
        // Release targets from losing collectors
        losingCollectorIds.forEach(collectorId => {
          const collector = this.collectors.get(collectorId);
          if (collector) {
            collector.releaseTarget(txId);
          }
        });
        
        console.log(`Mining competition resolved for tx ${txId}: ${winningCollectorId} won against ${losingCollectorIds.join(', ')}`);
      }
    });
  }

  private determineCompetitionWinner(txId: string, competingCollectorIds: string[]): string {
    const store = useGalaxyStore.getState();
    const txState = store.blockchain.transactions.get(txId);
    
    if (!txState) return competingCollectorIds[0];
    
    let bestScore = -1;
    let winnerId = competingCollectorIds[0];
    
    competingCollectorIds.forEach(collectorId => {
      const collector = this.collectors.get(collectorId);
      if (!collector) return;
      
      let score = collector.miningPower; // Base score from mining power
      
      // Bonus for fee preference match
      const feeRate = txState.feeRate;
      switch (collector.config.feePreference) {
        case 'high':
          score += feeRate > 50 ? 0.5 : -0.2;
          break;
        case 'medium':
          score += (feeRate >= 20 && feeRate <= 100) ? 0.3 : -0.1;
          break;
        case 'low':
          score += feeRate < 30 ? 0.3 : -0.1;
          break;
        case 'any':
          score += 0.1; // Small bonus for flexibility
          break;
      }
      
      // Penalty for high beam utilization (spread mining efforts)
      const status = collector.getMiningStatus();
      score -= status.beamUtilization * 0.3;
      
      // Competition intensity factor
      score *= (1 + this.config.competitionIntensity);
      
      if (score > bestScore) {
        bestScore = score;
        winnerId = collectorId;
      }
    });
    
    return winnerId;
  }

  /**
   * Phase 3: Connect with DynamicObjectsManager for ship lifecycle updates
   */
  public integrateWithShipManagement(availableShips: Map<string, TransactionShip>): void {
    const store = useGalaxyStore.getState();
    const mempoolNebula = store.scene.activeMempoolNebula;
    
    if (!mempoolNebula) return;
    
    // Provide ship data to collectors
    this.collectors.forEach(collector => {
      collector.scanForTargets(mempoolNebula, availableShips);
    });
    
    // Update transaction lifecycle states when ships are selected
    this.updateTransactionLifecycleStates();
  }

  private updateTransactionLifecycleStates(): void {
    const store = useGalaxyStore.getState();
    
    this.collectors.forEach(collector => {
      const status = collector.getMiningStatus();
      
      // Update state for selected transactions
      // Implementation would require exposing active targets from MiningCollector
      // For now, this serves as the integration point
    });
  }

  private updateMiningStatistics(): void {
    // Track mining efficiency and pool performance
    let totalActiveTargets = 0;
    let totalBeamUtilization = 0;
    
    this.collectors.forEach(collector => {
      const status = collector.getMiningStatus();
      totalActiveTargets += status.activeTargets;
      totalBeamUtilization += status.beamUtilization;
    });
    
    // Store statistics for HUD display
    const avgBeamUtilization = totalBeamUtilization / this.collectors.size;
    
    // Update store with mining metrics (would need to add to state schema)
    // store.updateMiningMetrics({
    //   activeExtractions: totalActiveTargets,
    //   averageBeamUtilization: avgBeamUtilization,
    //   poolStatistics: this.miningStatistics.poolSuccessRates
    // });
  }

  /**
   * Get current mining operations status for debugging/UI
   */
  public getMiningOperationsStatus(): {
    activeCollectors: number;
    totalActiveTargets: number;
    averageBeamUtilization: number;
    competitionLevel: number;
    poolPerformance: Array<{
      poolName: string;
      miningPower: number;
      activeTargets: number;
      beamUtilization: number;
    }>;
  } {
    const poolPerformance = Array.from(this.collectors.values()).map(collector => {
      const status = collector.getMiningStatus();
      return {
        poolName: collector.config.poolName,
        miningPower: status.totalMiningPower,
        activeTargets: status.activeTargets,
        beamUtilization: status.beamUtilization
      };
    });
    
    const totalActiveTargets = poolPerformance.reduce((sum, pool) => sum + pool.activeTargets, 0);
    const avgBeamUtilization = poolPerformance.reduce((sum, pool) => sum + pool.beamUtilization, 0) / poolPerformance.length;
    
    return {
      activeCollectors: this.collectors.size,
      totalActiveTargets,
      averageBeamUtilization: avgBeamUtilization,
      competitionLevel: this.config.competitionIntensity,
      poolPerformance
    };
  }

  public setQualityLevel(level: 'low' | 'medium' | 'high'): void {
    // Adjust visual quality of mining operations
    const _beamOpacity = level === 'high' ? 1.0 : level === 'medium' ? 0.7 : 0.5;
    
    this.collectors.forEach(_collector => {
      // Would need to expose setQualityLevel method on MiningCollector
      // collector.setQualityLevel(level, beamOpacity);
    });
  }

  /**
   * Validate that all mining collectors are properly configured for interaction
   */
  private validateInteractiveObjects(): void {
    console.log('🔍 Validating mining collector interactivity...');
    
    this.collectors.forEach(collector => {
      const group = collector.getMesh();
      
      // Find the actual mesh within the group that has userData
      let interactiveMesh: THREE.Mesh | null = null;
      group.traverse((child) => {
        if (child instanceof THREE.Mesh && child.userData['type'] === 'mining-collector') {
          interactiveMesh = child;
        }
      });
      
      if (!interactiveMesh) {
        console.error(`Mining collector ${collector.config.poolName} missing interactive mesh`);
        return;
      }
      
      const userData = interactiveMesh.userData;
      
      if (!userData['id']) {
        console.error(`Mining collector ${collector.config.poolName} missing userData.id`);
      }
      
      if (!userData['type']) {
        console.error(`Mining collector ${collector.config.poolName} missing userData.type`);
      }
      
      if (userData['type'] !== 'mining-collector') {
        console.error(`Mining collector ${collector.config.poolName} has incorrect type: ${userData['type']}`);
      }
      
      if (!userData['interactive']) {
        console.warn(`Mining collector ${collector.config.poolName} missing userData.interactive flag`);
      }
      
      console.log(`✅ Mining collector ${collector.config.poolName} validation passed:`, {
        id: userData['id'],
        type: userData['type'],
        interactive: userData['interactive'],
        meshType: interactiveMesh.constructor.name,
        position: group.position
      });
    });
    
    console.log(`🎯 All ${this.collectors.size} mining collectors validated for interactivity`);
  }

  public dispose(): void {
    // Clean up all collectors
    this.collectors.forEach(collector => {
      collector.dispose();
    });
    this.collectors.clear();
    
    // Remove from scene
    this.scene.remove(this.miningGroup);
    
    // Clear competition tracking
    this.transactionCompetition.clear();
  }
}